import { computed } from "@angular/core";
import { signalStore, withState, withComputed, withMethods, patchState } from "@ngrx/signals";
import { Invoice, InvoiceNamesAndIds, LineItem, Occurrence } from "../../pojo/Pricing";
import { EventI } from "../../pojo/EventI";
import { S25Util } from "../../util/s25-util";
import { PaymentSummaryData } from "./s25-payments/s25.payments.component";
import { IPaymentDetails } from "./s25-payments/payment.service";

interface S25PricingState {
    invoices: Invoice[];
    occurrences: Occurrence[];
    locatorMap: Record<number, string>;
    invoiceSummaryData: InvoiceNamesAndIds[];
}

const initialState: S25PricingState = {
    invoices: [],
    occurrences: [],
    locatorMap: {},
    invoiceSummaryData: [],
};

export const S25PricingStore = signalStore(
    withState(initialState),
    withComputed((store) => ({
        occMap: computed(
            () =>
                store.occurrences().reduce((map: Record<number, Occurrence>, occurrence: Occurrence) => {
                    map[occurrence.rsrvId] = occurrence;
                    return map;
                }, {}) ?? {},
        ),
        billNameMap: computed(() =>
            store.invoiceSummaryData().reduce(
                (map: Record<number | "eventType", Set<string>>, invoice) => {
                    [...invoice.rsrvIds].forEach((occId) => {
                        if (!map[occId]) map[occId] = new Set();
                        map[occId].add(invoice.name);
                    });

                    [...invoice.reqIds].forEach((reqId) => {
                        if (!map[reqId]) map[reqId] = new Set();
                        map[reqId].add(invoice.name);
                    });

                    if (invoice.includeEventType) map.eventType.add(invoice.name);
                    return map;
                },
                <Record<number | "eventType", Set<string>>>{ eventType: new Set() },
            ),
        ),
        paymentSummary: computed(() =>
            store.invoices().reduce((map: Record<number, PaymentSummaryData>, invoice: Invoice) => {
                invoice.payments?.forEach((payment: IPaymentDetails) => {
                    if (!map[payment.organizationId]) map[payment.organizationId] = [];
                    map[payment.organizationId].push({
                        ...payment,
                        invoiceId: invoice.id,
                        invoiceName: invoice.billDefn.billName,
                    });
                });
                return map;
            }, {}),
        ),
    })),
    withMethods((store) => ({
        setOccurrences(occurrences: Occurrence[]) {
            patchState(store, { occurrences: occurrences ?? [] });
        },

        setLocatorMap(events: EventI[]) {
            const locatorMap = events?.reduce((acc: { [key: number]: string }, event: EventI) => {
                acc[event.event_id] = event.event_locator;
                return acc;
            }, {});
            patchState(store, { locatorMap: locatorMap ?? {} });
        },

        setInvoiceSummaryData(data: InvoiceNamesAndIds[]) {
            patchState(store, { invoiceSummaryData: data });
        },

        setInvoices(invoices: Invoice[]) {
            patchState(store, { invoices: invoices ?? [] });
        },

        updateInvoiceName(invoiceId: number, newName: string) {
            patchState(store, {
                invoices: store.invoices().map((invoice) => {
                    if (invoice.id === invoiceId) {
                        return { ...invoice, billDefn: { ...invoice.billDefn, billName: newName } };
                    }
                    return invoice;
                }),
                invoiceSummaryData: store.invoiceSummaryData().map((invoice) => {
                    if (invoice.id === invoiceId) {
                        return { ...invoice, name: newName };
                    }
                    return invoice;
                }),
            });
        },

        updateLineItemAdjustment(lineItem: LineItem, evBillId: number) {
            patchState(store, {
                invoiceSummaryData: store.invoiceSummaryData().map((invoice) => {
                    if (invoice.id === evBillId) {
                        let itemFound; // flag to determine whether to update an existing adjustment or create a new one
                        const updatedLineItems = invoice.lineItemAdjustments.map((item) => {
                            if (item.itemId === lineItem.itemId && item.itemType === lineItem.itemType) {
                                itemFound = true;
                                return {
                                    ...item,
                                    ...(S25Util.isDefined(lineItem.adjustmentAmt)
                                        ? { adjustmentAmt: lineItem.adjustmentAmt }
                                        : { adjustmentPercent: lineItem.adjustmentPercent }),
                                };
                            }
                            return item;
                        });
                        return {
                            ...invoice,
                            lineItemAdjustments: itemFound
                                ? updatedLineItems
                                : [
                                      ...updatedLineItems,
                                      {
                                          adjustmentAmt: lineItem.adjustmentAmt,
                                          adjustmentPercent: lineItem.adjustmentPercent,
                                          itemName: lineItem.itemName,
                                          itemId: lineItem.itemId,
                                          itemType: lineItem.itemType,
                                          billId: invoice.id,
                                          invoiceName: invoice.name ?? "",
                                          chargeToId: lineItem.chargeToId,
                                          profileId: lineItem.profileId,
                                          adjustmentName: lineItem.adjustmentName,
                                          rsrvIds: new Set(lineItem.occurrences?.map((occ) => occ.rsrvId) ?? []),
                                          newAdjustments: [],
                                      },
                                  ],
                        };
                    }
                    return invoice;
                }),
            });
        },
    })),
);
